import { useEffect, useMemo, useRef, useState } from 'react';
import type { MarkupNode } from '@/engine/narrative';
import { contarCaracteres, parseMarkup } from '@/engine/narrative';
import { MarkupSpan } from './MarkupText';
import { useMetaStore } from '@/engine/state';
import { useTextoResuelto } from '@/hooks/useTextoResuelto';
import { cn } from '@/utils/cn';

const MS_POR_CHAR: Record<string, number> = {
  lenta: 45,
  normal: 22,
  rapida: 10,
  instantanea: 0,
};

interface TypewriterTextProps {
  texto: string;
  onComplete?: () => void;
  iniciarCompleto?: boolean;
  className?: string;
}

export function TypewriterText({
  texto,
  onComplete,
  iniciarCompleto = false,
  className,
}: TypewriterTextProps) {
  const velocidad = useMetaStore((s) => s.settings.velocidadTexto);
  const msPorChar = MS_POR_CHAR[velocidad] ?? MS_POR_CHAR.normal;

  // Resolver `[if ...]...[/if]` contra el estado actual antes de parsear markup visual.
  const textoResuelto = useTextoResuelto(texto);
  const nodes = useMemo<MarkupNode[]>(() => parseMarkup(textoResuelto), [textoResuelto]);
  const total = useMemo(() => contarCaracteres(nodes), [nodes]);

  const [revelados, setRevelados] = useState(() =>
    iniciarCompleto || msPorChar === 0 ? total : 0,
  );

  const textoRef = useRef(textoResuelto);
  if (textoRef.current !== textoResuelto) {
    textoRef.current = textoResuelto;
    setRevelados(iniciarCompleto || msPorChar === 0 ? total : 0);
  }

  useEffect(() => {
    if (revelados >= total) {
      onComplete?.();
      return;
    }
    if (msPorChar === 0) {
      setRevelados(total);
      return;
    }
    const t = setTimeout(() => setRevelados((r) => Math.min(r + 1, total)), msPorChar);
    return () => clearTimeout(t);
  }, [revelados, total, msPorChar, onComplete]);

  const skip = () => {
    if (revelados < total) setRevelados(total);
  };

  let consumidos = 0;
  const spans = nodes.map((node, i) => {
    const inicio = consumidos;
    consumidos += node.value.length;
    const localMax = Math.max(0, revelados - inicio);
    return <MarkupSpan key={i} node={node} maxChars={localMax} />;
  });

  return (
    <p onClick={skip} className={cn('cursor-pointer select-text', className)} aria-live="polite">
      {spans}
    </p>
  );
}
